import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaThumbtack } from 'react-icons/fa';
import { PostCard } from '@/components/Post';
import { LoadingSpinner } from '@/components';
import { Post } from '@/redux/slices/dashboardSlice';
import api from '@/utils/api';
import './UserPostsFeed.scss';

interface UserPostsFeedProps {
  userId: string;
  isOwnProfile: boolean;
  pinnedPost: Post | null;
  onPinChange: (post: Post | null) => void;
  profileId?: number; // kept for API compat but no longer used locally
}

const UserPostsFeed: React.FC<UserPostsFeedProps> = ({
  userId,
  isOwnProfile,
  pinnedPost,
  onPinChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts]       = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore]   = useState(false);
  const [offset, setOffset]     = useState(0);
  const [error, setError]       = useState<string | null>(null);
  const [pinningId, setPinningId] = useState<string | null>(null);
  const LIMIT = 10;

  const fetchPosts = async (reset = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const currentOffset = reset ? 0 : offset;

      const response = await api.get(`/posts/user/${userId}`, {
        params: { limit: LIMIT, offset: currentOffset },
      });

      if (reset) {
        setPosts(response.data.posts);
        setOffset(LIMIT);
      } else {
        setPosts(prev => [...prev, ...response.data.posts]);
        setOffset(prev => prev + LIMIT);
      }
      setHasMore(response.data.hasMore);
    } catch {
      setError('Failed to load posts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchPosts(true); }, [userId]);

  // Refresh when returning from the create-post page
  useEffect(() => {
    if (!location.state?.postCreated) return;
    fetchPosts(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.postCreated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePin = async (post: Post) => {
    setPinningId(post.id);
    try {
      const { data } = await api.patch(`/posts/${post.id}/pin`);
      // data.pinned = true means this post is now pinned; false = unpinned
      onPinChange(data.pinned ? post : null);
    } catch {
      // silently fail — user can retry
    } finally {
      setPinningId(null);
    }
  };

  const isPinned = (postId: string) => pinnedPost?.id === postId;

  return (
    <div className="user-posts-feed">
      {/* Pinned post */}
      {pinnedPost && (
        <div className="pinned-post-section">
          <div className="pinned-post-label">
            <FaThumbtack className="pin-icon" />
            <span>Pinned post</span>
          </div>
          <div className="pinned-post-wrapper">
            <PostCard post={pinnedPost} />
            {isOwnProfile && (
              <button
                className="pin-toggle-btn pin-toggle-btn--active"
                onClick={() => handlePin(pinnedPost)}
                disabled={pinningId === pinnedPost.id}
                title="Unpin post"
              >
                <FaThumbtack /> Unpin
              </button>
            )}
          </div>
        </div>
      )}

      {/* Posts list */}
      {error && (
        <div className="posts-error">
          <p>{error}</p>
          <button onClick={() => fetchPosts(true)}>Try Again</button>
        </div>
      )}

      {isLoading && posts.length === 0 ? (
        <LoadingSpinner size="medium" message="Loading posts..." />
      ) : posts.length === 0 ? (
        <div className="posts-empty">
          <p>{isOwnProfile ? "You haven't posted anything yet." : 'No posts yet.'}</p>
          {isOwnProfile && <p className="empty-hint">Use the button below to create your first post!</p>}
        </div>
      ) : (
        <>
          <div className="posts-list">
            {posts.map((post) => (
              <div key={post.id} className="post-with-pin">
                <PostCard post={post} />
                {isOwnProfile && (
                  <button
                    className={`pin-toggle-btn ${isPinned(post.id) ? 'pin-toggle-btn--active' : ''}`}
                    onClick={() => handlePin(post)}
                    disabled={pinningId === post.id}
                    title={isPinned(post.id) ? 'Unpin post' : 'Pin to profile'}
                  >
                    <FaThumbtack />
                    {isPinned(post.id) ? 'Unpin' : 'Pin'}
                  </button>
                )}
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="posts-load-more">
              <button
                onClick={() => !isLoading && hasMore && fetchPosts()}
                disabled={isLoading}
                className="btn-load-more"
              >
                {isLoading ? 'Loading...' : 'Load More Posts'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserPostsFeed;
